// stores/insights.js
import { defineStore } from 'pinia';
import { getCookie, setCookie } from '~/composables/useCookieManager';

export const useInsightsStore = defineStore('insights', {
  state: () => ({
    _selectedOMSShop: [] as unknown[],
    _selectedCMSShop: {} as object,
    _selectedDateRange: [] as unknown[],
    _selectedPeriod: {} as object,
    _selectedShopGroup: [] as unknown[],
    _selectedShopOwner: [] as unknown[],
    _activeCategory: null as string | null,
    _fulfilmentIsActive: false,
    _customerCareIsActive: false,
  }),

  getters: {
    selectedOMSShop(state) {
      return state._selectedOMSShop;
    },
    selectedCMSShop(state) {
      return state._selectedCMSShop;
    },
    selectedDateRange(state) {
      return state._selectedDateRange;
    },
    selectedPeriod(state) {
      return state._selectedPeriod;
    },
    selectedShopGroup(state) {
      return state._selectedShopGroup;
    },
    selectedShopOwner(state) {
      return state._selectedShopOwner;
    },
    activeCategory(state) {
      return state._activeCategory;
    },
    fulfilmentIsActive(state) {
      return state._fulfilmentIsActive;
    },
    customerCareIsActive(state) {
      return state._customerCareIsActive;
    },
  },

  actions: {
    async updateSelectedOMSShop(data: unknown[]) {
      this._selectedOMSShop = data;
      saveDataToCookie('selectedOMSShop', data);
    },

    async updateSelectedShopOwner(data: unknown[]) {
      if (!data) {
        return;
      }

      this._selectedShopOwner = data;
      saveDataToCookie('selectedShopOwner', data);
    },

    updateSelectedCMSShop(data: object) {
      this._selectedCMSShop = data;
      saveDataToCookie('selectedCMSShop', data);
    },

    updateSelectedShopGroup(data: unknown[]) {
      this._selectedShopGroup = data;
      saveDataToCookie('selectedShopGroup', data);
    },

    updateSelectedDateRange(data: unknown[]) {
      this._selectedDateRange = data;
      saveDataToCookie('selectedDateRange', data);
    },

    updateSelectedPeriod(data: object) {
      this._selectedPeriod = data;
      saveDataToCookie('selectedPeriod', data);
    },

    updateActiveCategory(data: string | null) {
      this._activeCategory = data;
      saveDataToCookie('activeCategory', JSON.stringify(data));
    },

    getSelectedOMSShop() {
      const data = loadDataFromCookie('selectedOMSShop');
      if (data) {
        this._selectedOMSShop = data;
      }
    },

    getSelectedShopOwner() {
      const data = loadDataFromCookie('selectedShopOwner');
      if (data) {
        this._selectedShopOwner = data;
      }
    },

    getSelectedShopGroup() {
      const data = loadDataFromCookie('selectedShopGroup');
      if (data) {
        this._selectedShopGroup = data;
      }
    },

    getSelectedCMSShop() {
      const data = loadDataFromCookie('selectedCMSShop');
      if (data) {
        this._selectedCMSShop = data;
      }
    },

    getSelectedDateRange() {
      const data = loadDataFromCookie('selectedDateRange');
      if (data) {
        this._selectedDateRange = data;
      }
    },

    getSelectedPeriod() {
      const data = loadDataFromCookie('selectedPeriod');
      if (data) {
        this._selectedPeriod = data;
      }
    },

    getActiveCategory() {
      const data = loadDataFromCookie('activeCategory');
      if (data) {
        this._activeCategory = data;
      }
    },

    resetFilters() {
      // Reset all filters and selections to ensure clean state when switching companies
      this.updateSelectedOMSShop([]);
      this.updateSelectedCMSShop({});
      this.updateSelectedDateRange([]);
      this.updateSelectedPeriod({});
      this.updateSelectedShopGroup([]);
      this.updateSelectedShopOwner([]);
      this.updateActiveCategory(null);
    },
  },
});

// Helper functions for handling cookies
function saveDataToCookie(key: string, data: string | object | null) {
  if (data === null) {
    return setCookie(key, '');
  }
  return setCookie(key, data);
}

function loadDataFromCookie(key: string) {
  return getCookie(key);
}
